import { useState, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function PhotoGallery({ images = [], name = "" }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const scrollerRef = useRef(null);
  const displayImages = images.slice(0, 5);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const navigate = (dir) => {
    setCurrentIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return images.length - 1;
      if (next >= images.length) return 0;
      return next;
    });
  };

  const onMobileScroll = () => {
    if (!scrollerRef.current) return;
    const w = scrollerRef.current.clientWidth;
    const idx = Math.round(scrollerRef.current.scrollLeft / w);
    if (idx !== mobileIndex) setMobileIndex(idx);
  };

  if (!images.length) return null;

  return (
    <>
      {/* Desktop: Apple-style 3-column gallery grid (hidden on mobile) */}
      <div className="hidden md:block">
        <div data-testid="photo-gallery" className="gallery-grid rounded-3xl overflow-hidden">
          {displayImages.map((url, i) => (
            <button
              key={`photo-${url.slice(-20)}-${i}`}
              data-testid={`gallery-image-${i}`}
              onClick={() => openLightbox(i)}
              className="relative overflow-hidden cursor-pointer group"
            >
              <img
                src={url}
                alt={`${name} - Bild ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {i === displayImages.length - 1 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">
                    +{images.length - 5} Fotos
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile: Horizontal swipe carousel + dot indicator */}
      <div data-testid="photo-gallery-mobile" className="md:hidden relative">
        <div
          ref={scrollerRef}
          onScroll={onMobileScroll}
          className="flex overflow-x-auto snap-x snap-mandatory rounded-3xl scrollbar-hide"
          style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
        >
          {images.map((url, i) => (
            <button
              key={`m-photo-${url.slice(-20)}-${i}`}
              data-testid={`gallery-mobile-image-${i}`}
              onClick={() => openLightbox(i)}
              className="relative shrink-0 w-full h-[280px] snap-center"
            >
              <img
                src={url}
                alt={`${name} - Bild ${i + 1}`}
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>
        {/* Image counter pill */}
        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-md">
          {mobileIndex + 1} / {images.length}
        </div>
        {/* Dot indicators (max 7 dots) */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.slice(0, Math.min(images.length, 7)).map((_, i) => (
              <span
                key={`dot-${i}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === Math.min(mobileIndex, 6) ? "bg-white w-4" : "bg-white/50"}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 backdrop-blur-md transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            className="absolute left-4 md:left-6 text-white/80 hover:text-white p-3 rounded-full bg-white/10 backdrop-blur-md transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <img
            src={images[currentIndex]}
            alt={`${name} - Bild ${currentIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            className="absolute right-4 md:right-6 text-white/80 hover:text-white p-3 rounded-full bg-white/10 backdrop-blur-md transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="absolute bottom-6 text-white/60 text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
}
